import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@stores/authStore';
import { useAccessibilityRootClass } from '@hooks/useAccessibilityRootClass';
import { IdleLogout } from '@components/auth/IdleLogout';
import { applyUserLocale, getStoredUserLocale } from '@/lib/userLocale';
import { GlobalLoadingIndicator } from '@components/GlobalLoadingIndicator';
import MainLayout from '@components/layouts/MainLayout';
import AuthLayout from '@components/layouts/AuthLayout';
import LoginPage from '@features/auth/LoginPage';
import PlatformLoginPage from '@features/auth/PlatformLoginPage';
import PlatformRegisterPage from '@features/auth/PlatformRegisterPage';
import PlatformRegisterCompletePage from '@features/auth/PlatformRegisterCompletePage';
import SsoCallbackPage from '@features/auth/SsoCallbackPage';
import SetupAccountPage from '@features/auth/SetupAccountPage';
import ForgotPasswordPage from '@features/auth/ForgotPasswordPage';
import ResetPasswordPage from '@features/auth/ResetPasswordPage';
import PlatformLayout from '@features/platform/PlatformLayout';
import PlatformPlanRequestsPage from '@features/platform/PlatformPlanRequestsPage';
import PlatformPricingPage from '@features/platform/PlatformPricingPage';
import PlatformProfilePage from '@features/platform/PlatformProfilePage';
import PlatformAdminManagementPage from '@features/platform/PlatformAdminManagementPage';
import { TenantManagement } from '@components/settings/TenantManagement';
import DashboardPage from '@features/dashboard/DashboardPage';
import InboxPage from '@features/inbox/InboxPage';
import WhatsAppInboxPage from '@features/whatsapp/WhatsAppInboxPage';
import FacebookInboxPage from '@features/facebook/FacebookInboxPage';
import TicketsPage from '@features/tickets/TicketsPage';
import TicketsBoardPage from '@features/tickets/TicketsBoardPage';
import TicketDetailPage from '@features/tickets/TicketDetailPage';
import TicketFormPage from '@features/tickets/TicketFormPage';
import CustomersPage from '@features/customers/CustomersPage';
import CustomerProfilePage from '@features/customers/CustomerProfilePage';
import CreateCustomerPage from '@features/customers/CreateCustomerPage';
import ImportCustomersPage from '@features/customers/ImportCustomersPage';
import OrganizationsPage from '@features/customers/OrganizationsPage';
import OrganizationDetailPage from '@features/customers/OrganizationDetailPage';
import KnowledgeBasePage from '@features/knowledge/KnowledgeBasePage';
import ArticleEditorPage from '@features/knowledge/ArticleEditorPage';
import SettingsPage, { TenantSettingsPage, AuditLogsRedirect } from '@features/settings/SettingsPage';
import ReportsPage from '@features/reports/ReportsPage';
import ReportBuilder from '@features/reports/ReportBuilder';
import SavedReports from '@features/reports/SavedReports';

const ScheduledReports = lazy(() => import('@features/reports/ScheduledReports'));
const PeriodComparison = lazy(() => import('@features/reports/PeriodComparison'));

// Guide content is static and sizeable; lazy so it never sits in the initial bundle.
const HelpPage = lazy(() => import('@features/help/HelpPage'));
import { ConnectorsPage, IntegrationCatalogPage } from '@features/integrations';
import { HuhokaAuthorizePage, HuhokaCallbackPage } from '@features/integrations/huhoka';
import { safeReturnTo } from '@utils/returnTo';
import LiveChatPage from '@features/chat/LiveChatPage';
import LiveCallPage from '@features/voice/LiveCallPage';
import ActiveCallPage from '@features/voice/ActiveCallPage';
import BacktrackPage from '@features/tickets/BacktrackPage';
import PortalPreview from '@components/settings/PortalPreview';
import { ErrorPage } from '@components/ErrorPage';

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto"></div>
        <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * Like ProtectedRoute, but a signed-out visitor comes back to this exact URL
 * (query included) after signing in. Used where another product sends the
 * browser here with parameters that must survive the login, e.g. HuHoKa CRM's
 * connection request.
 */
/** The catch-all route's layout: the app shell when signed in, nothing when not. */
function NotFoundShell() {
  const { isAuthenticated, isLoading } = useAuthStore();
  if (isLoading) return <LoadingScreen />;
  return isAuthenticated ? <MainLayout /> : <ErrorPage />;
}

function ReturnAfterLoginRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }

  return <>{children}</>;
}

function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    // Platform users go to platform dashboard, tenant users go to main dashboard
    if (user?.isPlatformUser) {
      return <Navigate to="/platform/tenants" replace />;
    }
    const returnTo = safeReturnTo(new URLSearchParams(location.search).get('returnTo'));
    return <Navigate to={returnTo ?? '/dashboard'} replace />;
  }

  return <>{children}</>;
}

function PlatformProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  if (isLoading) {
    return <LoadingScreen />;
  }

  // To the normal sign-in, not the platform-admin one: the platform-admin URL
  // is deliberately not linked or redirected to from anywhere.
  if (!isAuthenticated || !user?.isPlatformUser) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function App() {
  const initialize = useAuthStore((state) => state.initialize);
  const hasHydrated = useAuthStore((state) => state._hasHydrated);

  // Apply global accessibility toggles (high contrast / reduced motion) to <html>.
  useAccessibilityRootClass();

  // #18930 — reflect the user's saved display language on <html lang> from the
  // cached locale, so a reload isn't stuck at lang="en" before the profile loads.
  useEffect(() => {
    applyUserLocale(getStoredUserLocale());
  }, []);

  useEffect(() => {
    // Only initialize after zustand persist has hydrated the state from localStorage
    if (hasHydrated) {
      initialize();
    }
  }, [hasHydrated, initialize]);

  return (
    <>
      <GlobalLoadingIndicator />
      <IdleLogout />
      <Routes>
        {/* Auth routes - redirect to dashboard if already logged in */}
        <Route
          element={
            <PublicOnlyRoute>
              <AuthLayout />
            </PublicOnlyRoute>
          }
        >
          <Route path="/login" element={<LoginPage />} />
          {/* Platform admin: reached only by typing the URL — no link, menu
              entry or redirect points here. The old /platform-login is gone
              and falls through to the 404 page. */}
          <Route path="/platform-admin" element={<PlatformLoginPage />} />
          <Route path="/platform-admin/register" element={<PlatformRegisterPage />} />
          <Route path="/platform-admin/register/complete" element={<PlatformRegisterCompletePage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        </Route>

        {/* Emailed-link pages: open whether or not someone is signed in in
            this browser. Behind PublicOnlyRoute a signed-in user was sent to
            the dashboard and the link's token was lost, so a user who thinks
            their account is compromised could not reset from it (bug #20958). */}
        <Route element={<AuthLayout />}>
          <Route path="/setup-account" element={<SetupAccountPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* SSO landing. Deliberately OUTSIDE PublicOnlyRoute: this page has to
            redeem its single-use code even when a stale session is still in
            storage, and it sets auth state itself. Under PublicOnlyRoute the
            moment login() lands the guard would Navigate to /dashboard, losing
            the returnTo — or, with a pre-existing session, bounce away before
            the code was ever redeemed (and the code expires in 60s). */}
        <Route element={<AuthLayout />}>
          <Route path="/login/sso/callback" element={<SsoCallbackPage />} />
        </Route>

        {/* Platform admin routes */}
        <Route
          element={
            <PlatformProtectedRoute>
              <PlatformLayout />
            </PlatformProtectedRoute>
          }
        >
          <Route path="/platform" element={<Navigate to="/platform/tenants" replace />} />
          <Route path="/platform/tenants" element={<TenantManagement />} />
          <Route path="/platform/plan-requests" element={<PlatformPlanRequestsPage />} />
          <Route path="/platform/pricing" element={<PlatformPricingPage />} />
          <Route path="/platform/profile" element={<PlatformProfilePage />} />
          <Route path="/platform/admin-management" element={<PlatformAdminManagementPage />} />
        </Route>

        {/* Platform admin — standalone tenant settings (full screen, no layout wrapper) */}
        <Route
          path="/platform/tenants/:tenantId/settings/*"
          element={
            <PlatformProtectedRoute>
              <TenantSettingsPage standalone exitPath="/platform/tenants" />
            </PlatformProtectedRoute>
          }
        />

        {/* HuHoKa CRM link (plan 14) - full-page, no main layout. The approve
            page is where HuHoKa CRM sends an admin; the callback is where it
            sends them back after a connection started here. */}
        <Route
          path="/integrations/huhoka/authorize"
          element={
            <ReturnAfterLoginRoute>
              <HuhokaAuthorizePage />
            </ReturnAfterLoginRoute>
          }
        />
        <Route
          path="/integrations/huhoka/callback"
          element={
            <ReturnAfterLoginRoute>
              <HuhokaCallbackPage />
            </ReturnAfterLoginRoute>
          }
        />

        {/* Portal preview — full-page, no main layout */}
        <Route
          path="/portal-preview"
          element={
            <ProtectedRoute>
              <PortalPreview />
            </ProtectedRoute>
          }
        />

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/inbox" element={<InboxPage />} />
          <Route path="/whatsapp" element={<WhatsAppInboxPage />} />
          <Route path="/facebook" element={<FacebookInboxPage />} />
          <Route path="/tickets" element={<TicketsBoardPage />} />
          <Route path="/tickets/list" element={<TicketsPage />} />
          <Route path="/tickets/new" element={<TicketFormPage />} />
          <Route path="/tickets/:id/edit" element={<TicketFormPage />} />
          <Route path="/tickets/:id" element={<TicketDetailPage />} />
          <Route path="/backtrack" element={<BacktrackPage />} />
          {/* The audit log moved into Organization Settings; old links follow it. */}
          <Route path="/audit-logs" element={<AuditLogsRedirect />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/customers/new" element={<CreateCustomerPage />} />
          <Route path="/customers/import" element={<ImportCustomersPage />} />
          <Route path="/customers/:id" element={<CustomerProfilePage />} />
          <Route path="/organizations" element={<OrganizationsPage />} />
          <Route path="/organizations/:id" element={<OrganizationDetailPage />} />
          <Route path="/knowledge" element={<KnowledgeBasePage />} />
          <Route path="/knowledge/new" element={<ArticleEditorPage />} />
          <Route path="/knowledge/:id" element={<ArticleEditorPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/reports/builder" element={<ReportBuilder />} />
          <Route path="/reports/saved" element={<SavedReports />} />
          <Route path="/reports/scheduled" element={<Suspense fallback={<LoadingScreen />}><ScheduledReports /></Suspense>} />
          <Route path="/reports/comparison" element={<Suspense fallback={<LoadingScreen />}><PeriodComparison /></Suspense>} />
          {/* /integrations is the capability catalogue again (what dev shows);
              the connector grid lives at /integrations/connectors. The old
              /capabilities mount redirects for anyone holding a bookmark. */}
          <Route path="/integrations" element={<IntegrationCatalogPage />} />
          <Route path="/integrations/connectors" element={<ConnectorsPage />} />
          <Route path="/integrations/capabilities" element={<Navigate to="/integrations" replace />} />
          <Route path="/live-chat" element={<LiveChatPage />} />
          <Route path="/live-call" element={<LiveCallPage />} />
          <Route path="/live-call/active" element={<ActiveCallPage />} />
          <Route path="/settings/*" element={<SettingsPage />} />
          <Route
            path="/help"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <HelpPage />
              </Suspense>
            }
          />
        </Route>

        {/* 404 — show a friendly page instead of silently redirecting */}
        {/* Signed in, the not-found page keeps the sidebar and header: the
            shell is the layout and the message is its only child. Signed out
            there is no shell to keep, so the page stands alone. */}
        {/* No `index` child here: in react-router 6 an index under a "*"
            parent outranks the protected "/" route, so the bare domain 404'd
            instead of redirecting to /dashboard (or /login). */}
        <Route path="*" element={<NotFoundShell />}>
          <Route path="*" element={<ErrorPage embedded />} />
        </Route>
      </Routes>
    </>
  );
}

export default App;
